import { Box, Paper, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface MetricCardProps {
  label: string;
  value: number | string;
  icon?: React.ReactNode;
  tone?: 'default' | 'warning' | 'error' | 'success';
  to?: string;
  hint?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, tone = 'default', to, hint }) => {
  const accent =
    tone === 'warning'
      ? { color: 'warning.dark', bgcolor: 'warning.50' }
      : tone === 'error'
        ? { color: 'error.main', bgcolor: 'error.50' }
        : tone === 'success'
          ? { color: 'success.main', bgcolor: 'success.50' }
          : { color: 'primary.main', bgcolor: 'primary.50' };

  const card = (
    <Paper
      variant="outlined"
      title={hint}
      sx={{
        px: '24px',
        py: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        borderRadius: 2,
        height: '100%',
        minHeight: 96,
        textDecoration: 'none',
        color: 'inherit',
        ...(to && {
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          '&:hover': {
            borderColor: 'primary.200',
            bgcolor: 'primary.50',
          },
        }),
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...accent,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            display: 'block',
          }}
        >
          {value}
        </Typography>
        <Typography variant="textSM" color="text.secondary" noWrap sx={{ mt: '8px', display: 'block' }}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );

  if (!to) {
    return card;
  }

  return (
    <Box
      component={RouterLink}
      to={to}
      title={hint}
      sx={{ display: 'block', textDecoration: 'none', color: 'inherit', height: '100%' }}
    >
      {card}
    </Box>
  );
};

export default MetricCard;
